import { ReactNode, useEffect, useRef } from 'react';
import { RouterProvider } from 'react-router';
import i18n from 'i18next';
import { initReactI18next, I18nextProvider } from 'react-i18next';
import { Toaster } from 'sonner';
import { DevErrorPanel, DevErrorToast, devError } from './lib/dev-errors';
import { router } from './router';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TimezoneProvider } from './contexts/TimezoneContext';
import { LicenseProvider } from './contexts/LicenseContext';
import { WhitelabelProvider } from './contexts/WhitelabelContext';
import TRPCProvider from './components/TRPCProvider';
import { prefetchStaticData } from './lib/query-config';
import { queryClient, trpcClient } from './utils/api-client';
import enTranslations from './translations/en';
import ruTranslations from './translations/ru';
import kkTranslations from './translations/kk';
import arTranslations from './translations/ar';
import heTranslations from './translations/he';
import { SUPPORTED_LOCALES, RTL_LANGUAGES } from './utils/localization';
import { TranslationLoader } from './services/translationLoader';

// Initialize i18n
const getInitialLanguage = () => {
  const savedLanguage = localStorage.getItem('preferredLanguage');
  if (savedLanguage && SUPPORTED_LOCALES.includes(savedLanguage)) {
    return savedLanguage;
  }
  const browserLanguages = navigator.languages || [navigator.language];
  for (const browserLang of browserLanguages) {
    const langCode = browserLang.split('-')[0].toLowerCase();
    if (SUPPORTED_LOCALES.includes(langCode)) return langCode;
    const match = SUPPORTED_LOCALES.find(
      (s) => langCode.startsWith(s) || s.startsWith(langCode),
    );
    if (match) return match;
  }
  return 'en';
};

const updateDocumentDirection = (language: string) => {
  document.documentElement.dir = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';
  document.documentElement.lang = language;
};

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: enTranslations },
    ru: { translation: ruTranslations },
    kk: { translation: kkTranslations },
    ar: { translation: arTranslations },
    he: { translation: heTranslations },
  },
  lng: getInitialLanguage(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

const loadEnhancedTranslations = async (language: string) => {
  try {
    const staticTranslations = i18n.getResourceBundle(language, 'translation') || {};
    const dynamicTranslations = await TranslationLoader.loadTranslations(language);
    const mergedTranslations = TranslationLoader.mergeTranslations(
      TranslationLoader.flattenTranslations(staticTranslations),
      dynamicTranslations,
    );
    const nestedTranslations = TranslationLoader.unflattenTranslations(mergedTranslations);
    i18n.addResourceBundle(language, 'translation', nestedTranslations, true, true);
    return nestedTranslations;
  } catch (error) {
    console.warn(`Failed to load enhanced translations for ${language}:`, error);
    return i18n.getResourceBundle(language, 'translation') || {};
  }
};

function PrefetchManager({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const hasPrefetched = useRef(false);

  useEffect(() => {
    if (isLoading || hasPrefetched.current || !isAuthenticated) return;
    hasPrefetched.current = true;
    prefetchStaticData(queryClient, trpcClient as any).catch((e: unknown) =>
      devError(e, { location: 'App.prefetchStaticData' }),
    );
  }, [isAuthenticated, isLoading]);

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      hasPrefetched.current = false;
    }
  }, [isAuthenticated, isLoading]);

  return <>{children}</>;
}

function I18nInitializer({ children }: { children: ReactNode }) {
  useEffect(() => {
    const detectedLanguage = getInitialLanguage();
    if (i18n.language !== detectedLanguage) {
      i18n.changeLanguage(detectedLanguage);
    }
    if (!localStorage.getItem('preferredLanguage')) {
      localStorage.setItem('preferredLanguage', detectedLanguage);
    }
    updateDocumentDirection(i18n.language);
    loadEnhancedTranslations(i18n.language).catch((e: unknown) =>
      devError(e, { location: 'I18nInitializer.init' }),
    );
    TranslationLoader.preloadCommonLanguages().catch((e: unknown) =>
      devError(e, { location: 'I18nInitializer.preload' }),
    );

    const handleLanguageChange = async (lng: string) => {
      updateDocumentDirection(lng);
      localStorage.setItem('preferredLanguage', lng);
      await loadEnhancedTranslations(lng).catch((e: unknown) =>
        devError(e, { location: 'I18nInitializer.languageChanged' }),
      );
    };
    i18n.on('languageChanged', handleLanguageChange);

    const handleCustomLanguageEvent = async (event: CustomEvent) => {
      if (event.detail?.language) {
        const lang = event.detail.language;
        updateDocumentDirection(lang);
        await loadEnhancedTranslations(lang).catch((e: unknown) =>
          devError(e, { location: 'I18nInitializer.customLanguageEvent' }),
        );
        if (i18n.language === lang) {
          i18n.emit('languageChanged', lang);
        }
      }
    };
    window.addEventListener('languageChanged', handleCustomLanguageEvent as unknown as EventListener);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
      window.removeEventListener('languageChanged', handleCustomLanguageEvent as unknown as EventListener);
    };
  }, []);

  return <>{children}</>;
}

export default function App() {
  return (
    <TRPCProvider>
      <I18nextProvider i18n={i18n}>
        <I18nInitializer>
          <LicenseProvider>
            <AuthProvider>
              <PrefetchManager>
                <WhitelabelProvider>
                  <TimezoneProvider>
                    <RouterProvider router={router} />
                    <Toaster position="top-right" richColors />
                    {import.meta.env.DEV && <DevErrorPanel />}
                    {import.meta.env.DEV && <DevErrorToast />}
                  </TimezoneProvider>
                </WhitelabelProvider>
              </PrefetchManager>
            </AuthProvider>
          </LicenseProvider>
        </I18nInitializer>
      </I18nextProvider>
    </TRPCProvider>
  );
}
